import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { scrapbookEditRequest } from "./scrapbookEditRequest";
import { getSessionToken } from "../../lib/clerk";

// Create a custom base query that includes the Clerk token
const baseQueryWithAuth = async (args, api, extraOptions) => {
  const baseQuery = fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_URL || "http://localhost:5001/api",
    prepareHeaders: async (headers) => {
      try {
        const token = await getSessionToken();
        if (token) {
          headers.set("authorization", `Bearer ${token}`);
        }
        // Add CORS headers
        headers.set("Content-Type", "application/json");
        headers.set("Accept", "application/json");
      } catch (error) {
        console.error("Error getting auth token:", error);
      }
      return headers;
    },
  });

  return baseQuery(args, api, extraOptions);
};


export const api = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Individuals", "BulletinPosts", "FamilyTree", "MarketplaceItems", "ConnectProfiles", "Conversations", "UnreadCount", "AdminBulletinPosts", "AdminMarketplaceItems", "AdminConversations", "AdminStats", "AdminUsers", "BlockStatus", "RolodexNotes", "AdminNotifications", "FootprintArticles", "ScrapbookPosts"],
  endpoints: (builder) => ({
    // Individual endpoints
    getIndividual: builder.query({
      query: (id) => `individuals/${id}`,
      providesTags: (result, error, id) => [{ type: "Individuals", id }],
    }),
    getBulletinPosts: builder.query({
      query: () => `bulletin/bulletin_posts`,
      providesTags: ["BulletinPosts"],
    }),
    createBulletinPost: builder.mutation({
      query: (post) => ({
        url: `bulletin/bulletin_post`,
        method: "POST",
        body: post,
      }),
      invalidatesTags: ["BulletinPosts"],
    }),
    updateBulletinPost: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `bulletin/bulletin_post/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["BulletinPosts"],
    }),
    deleteBulletinPost: builder.mutation({
      query: ({ id, individual_id }) => ({
        url: `bulletin/bulletin_post/${id}?individual_id=${individual_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["BulletinPosts"],
    }),
    toggleBulletinReaction: builder.mutation({
      query: ({ postId, emoji }) => ({
        url: `bulletin/bulletin_post/${postId}/reaction`,
        method: "POST",
        body: { emoji },
      }),
      invalidatesTags: ["BulletinPosts"],
    }),
    getFamilyTree: builder.query({
      query: () => `family_tree`,
      providesTags: ["FamilyTree"],
    }),
    // The Family Footprint — read-only curated articles (stored as clean blocks).
    getFootprintArticles: builder.query({
      query: (section) =>
        `footprint/articles${section ? `?section=${section}` : ""}`,
      providesTags: ["FootprintArticles"],
    }),
    // The Scrapbook — the family photo wall (2026-07-09 meeting, section A).
    getScrapbookPosts: builder.query({
      query: ({ category, limit = 24, offset = 0 } = {}) => {
        const params = new URLSearchParams();
        if (category) params.set("category", category);
        params.set("limit", limit);
        params.set("offset", offset);
        return `scrapbook/posts?${params.toString()}`;
      },
      providesTags: ["ScrapbookPosts"],
    }),
    createScrapbookPost: builder.mutation({
      query: (body) => ({ url: `scrapbook/post`, method: "POST", body }),
      invalidatesTags: ["ScrapbookPosts"],
    }),
    updateScrapbookPost: builder.mutation({
      query: scrapbookEditRequest,
      invalidatesTags: ["ScrapbookPosts"],
    }),
    deleteScrapbookPost: builder.mutation({
      query: (postId) => ({ url: `scrapbook/post/${postId}`, method: "DELETE" }),
      invalidatesTags: ["ScrapbookPosts"],
    }),
    toggleScrapbookReaction: builder.mutation({
      query: ({ postId, emoji }) => ({
        url: `scrapbook/post/${postId}/reaction`,
        method: "POST",
        body: { emoji },
      }),
      invalidatesTags: ["ScrapbookPosts"],
    }),
    addScrapbookComment: builder.mutation({
      query: ({ postId, body }) => ({
        url: `scrapbook/post/${postId}/comment`,
        method: "POST",
        body: { body },
      }),
      invalidatesTags: ["ScrapbookPosts"],
    }),
    deleteScrapbookComment: builder.mutation({
      query: ({ commentId }) => ({
        url: `scrapbook/comment/${commentId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ScrapbookPosts"],
    }),
    getFootprintArticle: builder.query({
      query: (slug) => `footprint/article/${slug}`,
      providesTags: (result, error, slug) => [
        { type: "FootprintArticles", id: slug },
      ],
    }),
    getMarketplaceItems: builder.query({
      query: () => `marketplace/marketplace_items`,
      providesTags: ["MarketplaceItems"],
    }),
    createMarketplaceItem: builder.mutation({
      query: (item) => ({
        url: `marketplace/marketplace_item`,
        method: "POST",
        body: item,
      }),
      invalidatesTags: ["MarketplaceItems"],
    }),
    updateMarketplaceItem: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `marketplace/marketplace_item/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["MarketplaceItems"],
    }),
    deleteMarketplaceItem: builder.mutation({
      query: ({ id, individual_id }) => ({
        url: `marketplace/marketplace_item/${id}?individual_id=${individual_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["MarketplaceItems"],
    }),
    expressMarketplaceInterest: builder.mutation({
      query: ({ id, kind }) => ({
        url: `marketplace/marketplace_item/${id}/interest`,
        method: "POST",
        body: { kind },
      }),
    }),
    // AI endpoints
    askAi: builder.mutation({
      query: (data) => ({
        url: `ai/ask`,
        method: "POST",
        body: data,
      }),
    }),
    // Admin endpoints
    getAdminStatus: builder.query({
      query: (individualId) => `admin/status?individual_id=${individualId}`,
    }),
    getAdminStats: builder.query({
      query: (individualId) => `admin/stats?individual_id=${individualId}`,
      providesTags: ["AdminStats"],
    }),
    getAdminList: builder.query({
      query: (individualId) => `admin/admins?individual_id=${individualId}`,
    }),
    getAdminBulletinPosts: builder.query({
      query: (individualId) => `admin/bulletin-posts?individual_id=${individualId}`,
      providesTags: ["AdminBulletinPosts"],
    }),
    adminUpdateBulletinPost: builder.mutation({
      query: ({ id, individual_id, ...data }) => ({
        url: `admin/bulletin-post/${id}?individual_id=${individual_id}`,
        method: "PUT",
        body: { individual_id, ...data },
      }),
      invalidatesTags: ["AdminBulletinPosts", "BulletinPosts", "AdminStats"],
    }),
    adminDeleteBulletinPost: builder.mutation({
      query: ({ id, individual_id }) => ({
        url: `admin/bulletin-post/${id}?individual_id=${individual_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["AdminBulletinPosts", "BulletinPosts", "AdminStats"],
    }),
    adminPinBulletinPost: builder.mutation({
      query: ({ id, individual_id, is_pinned }) => ({
        url: `admin/bulletin-post/${id}/pin?individual_id=${individual_id}`,
        method: "PUT",
        body: { is_pinned },
      }),
      invalidatesTags: ["AdminBulletinPosts", "BulletinPosts"],
    }),
    getAdminMarketplaceItems: builder.query({
      query: (individualId) => `admin/marketplace-items?individual_id=${individualId}`,
      providesTags: ["AdminMarketplaceItems"],
    }),
    adminUpdateMarketplaceItem: builder.mutation({
      query: ({ id, individual_id, ...data }) => ({
        url: `admin/marketplace-item/${id}?individual_id=${individual_id}`,
        method: "PUT",
        body: { individual_id, ...data },
      }),
      invalidatesTags: ["AdminMarketplaceItems", "MarketplaceItems", "AdminStats"],
    }),
    adminDeleteMarketplaceItem: builder.mutation({
      query: ({ id, individual_id }) => ({
        url: `admin/marketplace-item/${id}?individual_id=${individual_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["AdminMarketplaceItems", "MarketplaceItems", "AdminStats"],
    }),
    getAdminUsers: builder.query({
      query: (individualId) => `admin/users?individual_id=${individualId}`,
      providesTags: ["AdminUsers"],
    }),
    adminWarnUser: builder.mutation({
      query: ({ target_id, individual_id, reason }) => ({
        url: `admin/user/${target_id}/warn`,
        method: "POST",
        body: { individual_id, reason },
      }),
      invalidatesTags: ["AdminUsers", "Conversations"],
    }),
    adminSetBlocks: builder.mutation({
      query: ({ target_id, blocks, reason }) => ({
        url: `admin/user/${target_id}/set-blocks`,
        method: "POST",
        body: { blocks, reason },
      }),
      invalidatesTags: ["AdminUsers", "BlockStatus"],
    }),
    adminRemoveWarnings: builder.mutation({
      query: ({ target_id }) => ({
        url: `admin/user/${target_id}/remove-warnings`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["AdminUsers", "Conversations"],
    }),
    getMyBlockStatus: builder.query({
      query: (individualId) => `messages/block-status?individual_id=${individualId}`,
      providesTags: ["BlockStatus"],
    }),
    submitAppeal: builder.mutation({
      query: (data) => ({
        url: `messages/appeal`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Conversations"],
    }),
    getAdminConversations: builder.query({
      query: (individualId) => `admin/conversations?individual_id=${individualId}`,
      providesTags: ["AdminConversations"],
    }),
    adminDeleteConversation: builder.mutation({
      query: ({ id, individual_id }) => ({
        url: `admin/conversation/${id}?individual_id=${individual_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["AdminConversations", "Conversations", "AdminStats"],
    }),
    // Connect profile endpoints
    getMyConnectProfile: builder.query({
      query: (individualId) =>
        `connect/my-profile?individual_id=${individualId}`,
      providesTags: ["ConnectProfiles"],
    }),
    getConnectProfiles: builder.query({
      query: (individualId) =>
        `connect/profiles?individual_id=${individualId}`,
      providesTags: ["ConnectProfiles"],
    }),
    getConnectDirectory: builder.query({
      query: (individualId) =>
        `connect/directory?individual_id=${individualId}`,
      providesTags: ["ConnectProfiles"],
    }),
    getConnectProfile: builder.query({
      query: ({ profileId, individualId }) =>
        `connect/profile/${profileId}?individual_id=${individualId}`,
      providesTags: (result, error, { profileId }) => [
        { type: "ConnectProfiles", id: profileId },
      ],
    }),
    upsertConnectProfile: builder.mutation({
      query: (profileData) => ({
        url: `connect/profile`,
        method: "POST",
        body: profileData,
      }),
      invalidatesTags: ["ConnectProfiles"],
    }),
    // Messaging / Correspondence endpoints
    getConversations: builder.query({
      query: (individualId) =>
        `messages/conversations?individual_id=${individualId}`,
      providesTags: ["Conversations"],
    }),
    getConversation: builder.query({
      query: ({ conversationId, individualId }) =>
        `messages/conversation/${conversationId}?individual_id=${individualId}`,
      providesTags: (result, error, { conversationId }) => [
        { type: "Conversations", id: conversationId },
      ],
    }),
    getUnreadCount: builder.query({
      query: (individualId) =>
        `messages/unread-count?individual_id=${individualId}`,
      providesTags: ["UnreadCount"],
    }),
    createConversation: builder.mutation({
      query: (data) => ({
        url: `messages/conversation`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Conversations", "UnreadCount"],
    }),
    sendMessage: builder.mutation({
      query: (data) => ({
        url: `messages/message`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, error, { conversation_id }) => [
        "Conversations",
        "UnreadCount",
        { type: "Conversations", id: conversation_id },
      ],
    }),
    deleteConversation: builder.mutation({
      query: ({ conversationId, individual_id }) => ({
        url: `messages/conversation/${conversationId}?individual_id=${individual_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Conversations", "UnreadCount"],
    }),
    markConversationRead: builder.mutation({
      query: ({ conversationId, individual_id }) => ({
        url: `messages/conversation/${conversationId}/read`,
        method: "PUT",
        body: { individual_id },
      }),
      invalidatesTags: ["Conversations", "UnreadCount"],
    }),
    toggleConversationPin: builder.mutation({
      query: ({ conversationId, individual_id }) => ({
        url: `messages/conversation/${conversationId}/pin`,
        method: "PUT",
        body: { individual_id },
      }),
      invalidatesTags: ["Conversations"],
    }),
    // Admin individual endpoints (tree editing)
    adminUpdateIndividual: builder.mutation({
      query: ({ id, individual_id, ...data }) => ({
        url: `admin/individual/${id}?individual_id=${individual_id}`,
        method: "PUT",
        body: { individual_id, ...data },
      }),
      invalidatesTags: ["FamilyTree", "AdminStats"],
    }),
    adminCreateIndividual: builder.mutation({
      query: ({ individual_id, ...data }) => ({
        url: `admin/individual?individual_id=${individual_id}`,
        method: "POST",
        body: { individual_id, ...data },
      }),
      invalidatesTags: ["FamilyTree", "AdminStats"],
    }),
    adminDeleteIndividual: builder.mutation({
      query: ({ id, individual_id }) => ({
        url: `admin/individual/${id}?individual_id=${individual_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["FamilyTree", "AdminStats"],
    }),
    adminUpdateRelationships: builder.mutation({
      query: ({ id, individual_id, ...data }) => ({
        url: `admin/individual/${id}/relationships?individual_id=${individual_id}`,
        method: "PUT",
        body: { individual_id, ...data },
      }),
      invalidatesTags: ["FamilyTree", "AdminStats"],
    }),

    // Rolodex Notes
    getRolodexNotes: builder.query({
      query: (individualId) => `rolodex/notes?individual_id=${individualId}`,
      providesTags: ["RolodexNotes"],
    }),
    saveRolodexNote: builder.mutation({
      query: (data) => ({
        url: `rolodex/notes`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["RolodexNotes"],
    }),

    // Admin Notification Center
    getAdminNotifications: builder.query({
      query: (status) =>
        status ? `admin/notifications?status=${status}` : `admin/notifications`,
      providesTags: ["AdminNotifications"],
    }),
    getAdminNotificationsUnreadCount: builder.query({
      query: () => `admin/notifications/unread_count`,
      providesTags: ["AdminNotifications"],
    }),
    updateAdminNotification: builder.mutation({
      query: ({ id, status }) => ({
        url: `admin/notifications/${id}`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: ["AdminNotifications"],
    }),
  }),
});

export const {
  useGetIndividualQuery,
  useGetBulletinPostsQuery,
  useCreateBulletinPostMutation,
  useUpdateBulletinPostMutation,
  useDeleteBulletinPostMutation,
  useToggleBulletinReactionMutation,
  useGetFamilyTreeQuery,
  useGetMarketplaceItemsQuery,
  useCreateMarketplaceItemMutation,
  useUpdateMarketplaceItemMutation,
  useDeleteMarketplaceItemMutation,
  useExpressMarketplaceInterestMutation,
  useGetMyConnectProfileQuery,
  useGetConnectProfilesQuery,
  useGetConnectDirectoryQuery,
  useGetConnectProfileQuery,
  useUpsertConnectProfileMutation,
  useAskAiMutation,
  useGetAdminStatusQuery,
  useGetAdminStatsQuery,
  useGetAdminListQuery,
  useGetAdminBulletinPostsQuery,
  useAdminUpdateBulletinPostMutation,
  useAdminDeleteBulletinPostMutation,
  useAdminPinBulletinPostMutation,
  useGetAdminMarketplaceItemsQuery,
  useAdminUpdateMarketplaceItemMutation,
  useAdminDeleteMarketplaceItemMutation,
  useGetAdminUsersQuery,
  useGetAdminConversationsQuery,
  useAdminDeleteConversationMutation,
  useGetConversationsQuery,
  useGetConversationQuery,
  useGetUnreadCountQuery,
  useCreateConversationMutation,
  useDeleteConversationMutation,
  useSendMessageMutation,
  useMarkConversationReadMutation,
  useToggleConversationPinMutation,
  useAdminUpdateIndividualMutation,
  useAdminCreateIndividualMutation,
  useAdminDeleteIndividualMutation,
  useAdminUpdateRelationshipsMutation,
  useAdminWarnUserMutation,
  useAdminSetBlocksMutation,
  useAdminRemoveWarningsMutation,
  useGetMyBlockStatusQuery,
  useSubmitAppealMutation,
  useGetRolodexNotesQuery,
  useGetAdminNotificationsQuery,
  useGetAdminNotificationsUnreadCountQuery,
  useUpdateAdminNotificationMutation,
  useSaveRolodexNoteMutation,
  useGetFootprintArticlesQuery,
  useGetFootprintArticleQuery,
  useGetScrapbookPostsQuery,
  useCreateScrapbookPostMutation,
  useUpdateScrapbookPostMutation,
  useDeleteScrapbookPostMutation,
  useToggleScrapbookReactionMutation,
  useAddScrapbookCommentMutation,
  useDeleteScrapbookCommentMutation,
} = api;
